import { useState, useEffect } from 'react'
import { useAuthStore } from '../../stores/authStore'
import { paymentService } from '../../services/paymentService'
import type {
  EarningsSummary,
  ExpertService,
  WithdrawalRequest,
  SeekerTransactionSummary,
} from '../../types/payment'
import {
  CreditCard,
  Wallet,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowDownLeft,
  Banknote,
  AlertCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'

export default function PaymentsPage() {
  const { user } = useAuthStore()
  const isTalent = user?.role === 'talent'

  // Expert state
  const [earnings, setEarnings] = useState<EarningsSummary | null>(null)
  const [services, setServices] = useState<ExpertService[]>([])
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([])

  // Seeker state
  const [transactionSummary, setTransactionSummary] = useState<SeekerTransactionSummary | null>(null)

  // UI state
  const [isLoading, setIsLoading] = useState(true)
  const [isWithdrawing, setIsWithdrawing] = useState(false)
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [withdrawAmount, setWithdrawAmount] = useState('')
  const [paymentDetails, setPaymentDetails] = useState('')

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTalent])

  const loadData = async () => {
    setIsLoading(true)
    try {
      if (isTalent) {
        const [earningsData, servicesData, withdrawalsData] = await Promise.all([
          paymentService.getEarningsSummary(),
          paymentService.getExpertServices(),
          paymentService.getWithdrawals(),
        ])
        setEarnings(earningsData)
        setServices(servicesData)
        setWithdrawals(withdrawalsData.results || [])
      } else {
        const data = await paymentService.getSeekerTransactions()
        setTransactionSummary(data)
      }
    } catch (err) {
      console.error('Error loading payment data:', err)
      toast.error('Failed to load payment data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleWithdraw = async () => {
    const amountCents = Math.round(parseFloat(withdrawAmount) * 100)
    if (isNaN(amountCents) || amountCents <= 0) {
      toast.error('Please enter a valid amount')
      return
    }

    if (earnings && amountCents > earnings.available_cents) {
      toast.error('Amount exceeds available balance')
      return
    }

    setIsWithdrawing(true)
    try {
      await paymentService.createWithdrawal(amountCents, paymentDetails)
      toast.success('Withdrawal request submitted!')
      setShowWithdrawModal(false)
      setWithdrawAmount('')
      setPaymentDetails('')
      loadData()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      toast.error(error.response?.data?.error || 'Failed to submit withdrawal request')
    } finally {
      setIsWithdrawing(false)
    }
  }

  const getWithdrawalStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string; icon: React.ElementType }> = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      approved: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle },
      completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
    }
    const style = styles[status] || styles.pending
    const Icon = style.icon
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  // Expert View
  if (isTalent) {
    const hasPendingWithdrawal = withdrawals.some(
      (w) => w.status === 'pending' || w.status === 'approved'
    )

    return (
      <div>
        <div className="mb-8">
          <h1 className="text-2xl font-bold">Earnings</h1>
          <p className="text-secondary-600">View your earnings and request withdrawals</p>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="card bg-primary-600 text-white">
            <div className="card-body">
              <div className="flex items-center gap-2 text-primary-200 text-sm">
                <TrendingUp className="h-4 w-4" />
                Total Earned
              </div>
              <p className="text-3xl font-bold mt-1">{earnings?.total_earned_display || '€0.00'}</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="flex items-center gap-2 text-secondary-500 text-sm">
                <Wallet className="h-4 w-4" />
                Available to Withdraw
              </div>
              <p className="text-3xl font-bold mt-1 text-green-600">
                {earnings?.available_display || '€0.00'}
              </p>
              <button
                onClick={() => setShowWithdrawModal(true)}
                disabled={!earnings || earnings.available_cents <= 0 || hasPendingWithdrawal}
                className="btn btn-accent mt-3 w-full disabled:opacity-50"
              >
                {hasPendingWithdrawal ? 'Withdrawal Pending' : 'Request Withdrawal'}
              </button>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="flex items-center gap-2 text-secondary-500 text-sm">
                <Clock className="h-4 w-4" />
                Pending (In Progress)
              </div>
              <p className="text-3xl font-bold mt-1 text-yellow-600">
                {earnings?.pending_display || '€0.00'}
              </p>
              <p className="text-xs text-secondary-500 mt-3">
                Available after services are completed
              </p>
            </div>
          </div>
        </div>

        {/* Services List */}
        <div className="card mb-8">
          <div className="card-header">
            <h2 className="font-semibold">Services Provided</h2>
          </div>
          <div className="card-body">
            {services.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-secondary-500">
                <Banknote className="h-12 w-12 mb-2 text-secondary-300" />
                <p>No services yet</p>
                <p className="text-sm">Your earnings will appear here after completing services</p>
              </div>
            ) : (
              <div className="divide-y divide-secondary-100">
                {services.map((service) => (
                  <div key={service.id} className="py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-green-100 text-green-600">
                        <ArrowDownLeft className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-medium">{service.service_type}</p>
                        <p className="text-sm text-secondary-500">
                          {service.seeker_name} &bull; {formatDate(service.paid_at)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-green-600">{service.payout_display}</p>
                      <p className="text-xs text-secondary-500">
                        Fee: €{(service.platform_fee_cents / 100).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Withdrawal History */}
        {withdrawals.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold">Withdrawal History</h2>
            </div>
            <div className="card-body">
              <div className="divide-y divide-secondary-100">
                {withdrawals.map((withdrawal) => (
                  <div key={withdrawal.id} className="py-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium">Withdrawal Request</p>
                      <p className="text-sm text-secondary-500">{formatDate(withdrawal.created_at)}</p>
                      {withdrawal.rejection_reason && (
                        <p className="text-sm text-red-600 mt-1">{withdrawal.rejection_reason}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{withdrawal.amount_display}</p>
                      {getWithdrawalStatusBadge(withdrawal.status)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Withdrawal Modal */}
        {showWithdrawModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="card max-w-md w-full">
              <div className="card-header">
                <h3 className="font-semibold">Request Withdrawal</h3>
              </div>
              <div className="card-body space-y-4">
                <div className="p-3 bg-blue-50 rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-700">
                    Available balance: <strong>{earnings?.available_display}</strong>
                  </p>
                </div>

                <div>
                  <label className="label">Amount (€)</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    placeholder="0.00"
                    className="input"
                  />
                </div>

                <div>
                  <label className="label">Bank Details / Payment Info</label>
                  <textarea
                    value={paymentDetails}
                    onChange={(e) => setPaymentDetails(e.target.value)}
                    placeholder="Enter your bank account details or preferred payment method..."
                    className="input min-h-[100px]"
                  />
                  <p className="text-xs text-secondary-500 mt-1">
                    Our team will process your withdrawal manually
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowWithdrawModal(false)}
                    className="btn btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleWithdraw}
                    disabled={isWithdrawing || !withdrawAmount}
                    className="btn btn-accent flex-1"
                  >
                    {isWithdrawing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Submit Request'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Seeker View
  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Payments</h1>
        <p className="text-secondary-600">View your payment history</p>
      </div>

      {/* Total Spent Card */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <div className="card bg-primary-600 text-white">
          <div className="card-body">
            <div className="flex items-center gap-2 text-primary-200 text-sm">
              <CreditCard className="h-4 w-4" />
              Total Spent
            </div>
            <p className="text-3xl font-bold mt-1">
              {transactionSummary?.total_spent_display || '€0.00'}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-2 text-secondary-500 text-sm">
              <CheckCircle className="h-4 w-4" />
              Transactions
            </div>
            <p className="text-3xl font-bold mt-1">
              {transactionSummary?.transactions.length || 0}
            </p>
            <p className="text-xs text-secondary-500 mt-3">Completed payments</p>
          </div>
        </div>
      </div>

      {/* Transaction List */}
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold">Transaction History</h2>
        </div>
        <div className="card-body">
          {!transactionSummary || transactionSummary.transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-secondary-500">
              <CreditCard className="h-12 w-12 mb-2 text-secondary-300" />
              <p>No transactions yet</p>
              <p className="text-sm">Your payments will appear here</p>
            </div>
          ) : (
            <div className="divide-y divide-secondary-100">
              {transactionSummary.transactions.map((transaction) => (
                <div key={transaction.id} className="py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-primary-100 text-primary-600">
                      <CreditCard className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {transaction.service_info
                          ? `Service with ${transaction.service_info.expert_name}`
                          : transaction.description}
                      </p>
                      <p className="text-sm text-secondary-500">
                        {transaction.service_info?.service_type && (
                          <span>{transaction.service_info.service_type} &bull; </span>
                        )}
                        {formatDate(transaction.completed_at || transaction.created_at)}
                      </p>
                    </div>
                  </div>
                  <p className="font-medium">{transaction.amount_display}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
